import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkObjectFieldDefinition = Awaited<ReturnType<typeof trpc.workObjectFields.listDefinitions.query>>[number];
export type EditableWorkObjectFieldType =
  | "text"
  | "textarea"
  | "select"
  | "multi_select"
  | "number"
  | "currency"
  | "percent"
  | "rating"
  | "duration"
  | "formula"
  | "rollup"
  | "date"
  | "date_range"
  | "checkbox"
  | "url"
  | "email"
  | "phone"
  | "user_relation"
  | "user_multi_relation"
  | "work_object_relation"
  | "work_object_multi_relation";
export type WorkObjectFieldLocationScopeType = "workspace" | "space" | "folder" | "board";

export type CreateWorkObjectFieldInput = {
  config?: Record<string, unknown>;
  key: string;
  name: string;
  options?: WorkObjectFieldOptionInput[];
  type: EditableWorkObjectFieldType;
};

export type UpdateWorkObjectFieldInput = {
  id: string;
  key: string;
  name: string;
};

export type WorkObjectFieldLocationInput = {
  isRequired: boolean;
  scopeId: string;
  scopeType: WorkObjectFieldLocationScopeType;
};

export type UpdateWorkObjectFieldConfigurationInput = {
  applicableTypeIds: string[];
  appliesToAllTypes: boolean;
  config?: Record<string, unknown>;
  id: string;
  locations: WorkObjectFieldLocationInput[];
  options?: WorkObjectFieldOptionInput[];
  relationTargetTypeIds?: string[];
};

export type WorkObjectFieldOptionInput = {
  color: string;
  id?: string;
  rank: string;
  value: string;
};

export type ArchiveWorkObjectFieldOptionInput = {
  fieldId: string;
  optionId: string;
  replacementOptionId?: string | null;
};

function sortFields(fields: WorkObjectFieldDefinition[]) {
  return [...fields].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function useWorkObjectFields() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const fieldsQuery = useQuery(trpc.workObjectFields.listDefinitions.queryOptions());
  const createMutation = useMutation(trpc.workObjectFields.create.mutationOptions());
  const updateMutation = useMutation(trpc.workObjectFields.update.mutationOptions());
  const updateConfigurationMutation = useMutation(trpc.workObjectFields.updateConfiguration.mutationOptions());
  const archiveMutation = useMutation(trpc.workObjectFields.archive.mutationOptions());
  const archiveOptionMutation = useMutation(trpc.workObjectFields.archiveOption.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      await fieldsQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load fields.");
    }
  }, [fieldsQuery, trpc]);

  async function createField(input: CreateWorkObjectFieldInput) {
    setError(null);
    try {
      const created = await createMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectFieldDefinition[]>(trpc.workObjectFields.listDefinitions.queryKey(), (current = []) => sortFields([...current, created]));
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create field.");
      throw err;
    }
  }

  async function updateField(input: UpdateWorkObjectFieldInput) {
    setError(null);
    try {
      const updated = await updateMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectFieldDefinition[]>(trpc.workObjectFields.listDefinitions.queryKey(), (current = []) =>
        current.map((field) => (field.id === updated.id ? updated : field)),
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update field.");
      throw err;
    }
  }

  async function updateFieldConfiguration(input: UpdateWorkObjectFieldConfigurationInput) {
    setError(null);
    try {
      const updated = await updateConfigurationMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectFieldDefinition[]>(trpc.workObjectFields.listDefinitions.queryKey(), (current = []) =>
        current.map((field) => (field.id === updated.id ? updated : field)),
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update field configuration.");
      throw err;
    }
  }

  async function archiveField(id: string) {
    setError(null);
    try {
      await archiveMutation.mutateAsync({ id });
      queryClient.setQueryData<WorkObjectFieldDefinition[]>(trpc.workObjectFields.listDefinitions.queryKey(), (current = []) => current.filter((field) => field.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive field.");
      throw err;
    }
  }

  async function archiveFieldOption(input: ArchiveWorkObjectFieldOptionInput) {
    setError(null);
    try {
      const updated = await archiveOptionMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectFieldDefinition[]>(trpc.workObjectFields.listDefinitions.queryKey(), (current = []) =>
        current.map((field) => (field.id === updated.id ? updated : field)),
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive field option.");
      throw err;
    }
  }

  return {
    archiveField,
    archiveFieldOption,
    createField,
    data: fieldsQuery.data ?? [],
    error: error ?? (fieldsQuery.error instanceof Error ? fieldsQuery.error.message : fieldsQuery.error ? String(fieldsQuery.error) : null),
    isArchiving: archiveMutation.isPending,
    isCreating: createMutation.isPending,
    isLoading: fieldsQuery.isPending,
    isUpdating: updateMutation.isPending || updateConfigurationMutation.isPending || archiveOptionMutation.isPending,
    reload,
    updateField,
    updateFieldConfiguration,
  };
}
